import React from 'react';
import { Button } from '../../../../../../ui';
import {
  DocumentConfiguration,
  DocumentRequirementStatus,
} from '../../../../../../utils/types/JobOpenings';

export const DEFAULT_DOCUMENTS = [
  {
    type: 'Resume / CV',
    description:
      'A standard professional summary of work experience and education.',
    mandatoryFixed: true,
  },
  {
    type: 'Cover Letter',
    description:
      'An introductory letter explaining the candidate\'s motivation.',
  },
  {
    type: 'Academic Transcript',
    description:
      'Official records showing completed courses and grades from educational institutions.',
  },
  {
    type: 'Tertiary Certificates',
    description:
      'Certificates obtained from higher education institutions like universities or colleges.',
  },
  {
    type: 'Professional Certifications',
    description:
      'Industry-specific certificates or professional licenses demonstrating expertise.',
  },
  {
    type: 'Reference Letters',
    description:
      'Letters from former employers or colleagues providing professional recommendations.',
  },
  {
    type: 'Work Portfolio',
    description:
      'Samples of previous projects, case studies, or links to individual repositories.',
  },
];

interface DocumentConfigurationModalProps {
  isOpen: boolean;
  onClose: () => void;
  editable: boolean;
  documentConfigurations: DocumentConfiguration[];
  onDocumentRequirementChange: (
    type: string,
    requirement: DocumentRequirementStatus,
  ) => void;
  onAllowMultipleChange: (type: string, allowMultiple: boolean) => void;
  onMakeAllMandatory: () => void;
  onMakeAllOptional: () => void;
  onMakeAllDisabled: () => void;
}

export const DocumentConfigurationModal: React.FC<DocumentConfigurationModalProps> = ({
  isOpen,
  onClose,
  editable,
  documentConfigurations,
  onDocumentRequirementChange,
  onAllowMultipleChange,
  onMakeAllMandatory,
  onMakeAllOptional,
  onMakeAllDisabled,
}) => {
  if (!isOpen) return null;

  return (
    <div className='fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4'>
      <div className='bg-white rounded-2xl w-full max-w-4xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh]'>
        { /* Modal Header */ }
        <div className='px-6 py-4 border-b border-slate-100 flex items-center justify-between bg-slate-50/50'>
          <div>
            <h3 className='text-lg font-bold text-slate-800 tracking-tight'>
              Required Documents Configuration
            </h3>
            <p className='text-xs text-slate-500 mt-0.5'>
              Define which documents candidates must provide and their requirements.
            </p>
          </div>
          <button
              onClick={onClose}
              className='p-2 hover:bg-slate-100 rounded-full transition-colors text-slate-400 hover:text-slate-600'
          >
            <svg
                xmlns='http://www.w3.org/2000/svg'
                className='h-5 w-5'
                fill='none'
                viewBox='0 0 24 24'
                stroke='currentColor'
            >
              <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth={2}
                  d='M6 18L18 6M6 6l12 12'
              />
            </svg>
          </button>
        </div>

        { /* Modal Body */ }
        <div className='flex-1 overflow-y-auto p-6'>
          <div className='rounded-xl border border-slate-200 overflow-hidden shadow-sm'>
            { /* Table Header */ }
            <div className='grid grid-cols-[1fr_120px_120px_120px_120px] items-center bg-slate-50 border-b border-slate-200 px-4 py-3 gap-4'>
              <span className='text-[11px] font-bold text-slate-400 uppercase tracking-widest text-left'>
                Document
              </span>
              { (['Mandatory', 'Optional', 'Disabled'] as const).map((status) => (
                <button
                    key={status}
                    disabled={!editable}
                    onClick={() => {
                      if (status === 'Mandatory') onMakeAllMandatory();
                      else if (status === 'Optional') onMakeAllOptional();
                      else onMakeAllDisabled();
                    }}
                    className={`w-full text-[11px] font-bold uppercase tracking-widest px-2 py-1.5 rounded-lg transition-all disabled:opacity-40 disabled:cursor-not-allowed text-left ${
                      status === 'Mandatory'
                        ? 'text-emerald-600 hover:bg-emerald-50'
                        : status === 'Optional'
                          ? 'text-blue-500 hover:bg-blue-50'
                          : 'text-slate-500 hover:bg-slate-100'
                    }`}
                >
                  { status === 'Mandatory'
                    ? '● Mandatory'
                    : status === 'Optional'
                      ? '● Optional'
                      : '● Disable' }
                </button>
              )) }
              <span className='text-[11px] font-bold text-slate-400 uppercase tracking-widest text-left'>
                Allow Multiple
              </span>
            </div>

            { /* Table Rows */ }
            <div className='divide-y divide-slate-100'>
              { DEFAULT_DOCUMENTS.map((doc, idx) => {
                const config = documentConfigurations?.find(
                  (c) => c.type === doc.type,
                );
                const requirement: DocumentRequirementStatus =
                  config?.requirement || 'Disabled';
                const isLocked = !!(doc as any).mandatoryFixed;
                const isDisabled = !editable || isLocked;
                const isEven = idx % 2 === 0;

                const allowsMultipleToggle =
                  doc.type !== 'Resume / CV' && doc.type !== 'Cover Letter';
                const allowMultiple = config?.allowMultiple ?? false;

                return (
                  <div
                      key={idx}
                      className={`grid grid-cols-[1fr_120px_120px_120px_120px] items-center px-4 py-4 gap-4 transition-colors ${
                        isEven ? 'bg-white' : 'bg-slate-50/30'
                      } ${requirement === 'Disabled' ? 'opacity-60' : ''}`}
                  >
                    { /* Document name */ }
                    <div className='flex items-center gap-2'>
                      <span
                          className={`text-sm font-semibold ${
                            requirement === 'Disabled'
                              ? 'text-slate-400'
                              : 'text-slate-700'
                          }`}
                      >
                        { doc.type }
                      </span>
                      { isLocked && (
                        <span className='text-[9px] font-bold text-emerald-600 bg-emerald-50 border border-emerald-200 px-1.5 py-0.5 rounded-full uppercase tracking-wide'>
                          Fixed
                        </span>
                      ) }
                    </div>

                    { /* Mandatory */ }
                    <label
                        className={`flex items-center justify-start gap-1.5 cursor-pointer select-none ${
                          isDisabled ? 'cursor-not-allowed' : ''
                        }`}
                    >
                      <input
                          type='radio'
                          name={`doc-modal-${doc.type}`}
                          value='Mandatory'
                          checked={requirement === 'Mandatory'}
                          disabled={isDisabled}
                          onChange={() =>
                            onDocumentRequirementChange(doc.type, 'Mandatory')
                          }
                          className='w-3.5 h-3.5 accent-emerald-500'
                      />
                      <span
                          className={`text-xs ${
                            requirement === 'Mandatory'
                              ? 'text-emerald-600 font-bold'
                              : 'text-slate-400'
                          }`}
                      >
                        Mandatory
                      </span>
                    </label>

                    { /* Optional */ }
                    <label
                        className={`flex items-center justify-start gap-1.5 cursor-pointer select-none ${
                          isDisabled ? 'cursor-not-allowed' : ''
                        }`}
                    >
                      <input
                          type='radio'
                          name={`doc-modal-${doc.type}`}
                          value='Optional'
                          checked={requirement === 'Optional'}
                          disabled={isDisabled}
                          onChange={() =>
                            onDocumentRequirementChange(doc.type, 'Optional')
                          }
                          className='w-3.5 h-3.5 accent-blue-500'
                      />
                      <span
                          className={`text-xs ${
                            requirement === 'Optional'
                              ? 'text-blue-600 font-bold'
                              : 'text-slate-400'
                          }`}
                      >
                        Optional
                      </span>
                    </label>

                    { /* Disabled */ }
                    <label
                        className={`flex items-center justify-start gap-1.5 cursor-pointer select-none ${
                          isDisabled ? 'cursor-not-allowed' : ''
                        }`}
                    >
                      <input
                          type='radio'
                          name={`doc-modal-${doc.type}`}
                          value='Disabled'
                          checked={requirement === 'Disabled'}
                          disabled={isDisabled}
                          onChange={() =>
                            onDocumentRequirementChange(doc.type, 'Disabled')
                          }
                          className='w-3.5 h-3.5 accent-slate-500'
                      />
                      <span
                          className={`text-xs ${
                            requirement === 'Disabled'
                              ? 'text-slate-600 font-bold'
                              : 'text-slate-400'
                          }`}
                      >
                        Disabled
                      </span>
                    </label>

                    { /* Allow Multiple Toggle */ }
                    <div className='flex items-center justify-start'>
                      { allowsMultipleToggle ? (
                        <button
                            type='button'
                            disabled={!editable || requirement === 'Disabled'}
                            onClick={() =>
                              onAllowMultipleChange(doc.type, !allowMultiple)
                            }
                            title={
                              allowMultiple
                                ? 'Allow Multiple: On'
                                : 'Allow Multiple: Off'
                            }
                            className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors duration-200 focus:outline-none disabled:opacity-40 disabled:cursor-not-allowed ${
                              allowMultiple ? 'bg-emerald-500' : 'bg-slate-300'
                            }`}
                        >
                          <span
                              className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform duration-200 ${
                                allowMultiple ? 'translate-x-4' : 'translate-x-0.5'
                              }`}
                          />
                        </button>
                      ) : (
                        <span className='text-[10px] text-slate-300 italic'>—</span>
                      ) }
                    </div>
                  </div>
                );
              }) }
            </div>
          </div>
        </div>

        { /* Modal Footer */ }
        <div className='px-6 py-4 border-t border-slate-100 bg-slate-50/50 flex justify-end gap-3'>
          <Button
              onClick={onClose}
              variant='secondary'
              label='Close'
              className='px-6'
          />
        </div>
      </div>
    </div>
  );
};

export default DocumentConfigurationModal;
